<script setup>
import { onMounted, onBeforeUnmount, ref, nextTick } from "vue";
import { useAuthorStore } from "@/stores/useAuthorStore";
import CustomTextarea from "../Generics/CustomTextarea.vue";
import ConfirmAuthorDuplicateModal from "./ConfirmAuthorDuplicateModal.vue";
import AuthorCardModal from "./AuthorCardModal.vue";
import { toTitleCase } from "@/utils/stringUtils";

import { useDraggableModal } from "@/composables/useDraggableModal";

const authorStore = useAuthorStore();

// 🔹 NOVO: arrastar o modal pelo cabeçalho (posição não persiste entre aberturas)
const { panelRef, panelStyle, isDragging, isDraggable, onHandlePointerDown } =
  useDraggableModal();

const obj = ref({
  authorId: 0,
  authorName: "",
  description: "",
});

const nameInput = ref(null);

const emit = defineEmits(["close", "refresh"]);

// ⬇️ ESTADO DE SUBMISSÃO
const isSubmitting = ref(false);

// ====================================================================
// ✅ TOAST PADRÃO (3000ms)
// ====================================================================
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error"); // "error" | "success"

// Ao salvar com sucesso, esconde só o painel do modal; o toast (num Teleport
// próprio) segue visível sozinho até o emit("close") desmontar o componente.
const hideBackdropForSuccess = ref(false);

// ✅ PATCH: ref do wrapper que contém o textarea real
const descriptionWrapRef = ref(null);


// ⬇️ NOVO: flag para saber se já foi confirmada a duplicidade
const allowDuplicateOnNextSubmit = ref(false);

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null;
  }, 3000);
}

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function showSuccessToast(message) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}
// ====================================================================
// 🔁 CONFIRMAÇÃO DE DUPLICIDADE (409) + CARD DO AUTOR
// ====================================================================

const showConfirmModal = ref(false);
const confirmMessage = ref("");
const existingAuthors = ref([]);

const showCardModal = ref(false);
const selectedAuthorId = ref(null);

function normalizeName() {
  obj.value.authorName = toTitleCase(
    (obj.value.authorName ?? "").toString().trim().replace(/\s+/g, " ")
  );
}

// ✅ PATCH: foco robusto no textarea/contenteditable interno do CustomTextarea
function focusDescriptionField() {
  nextTick(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const wrap = descriptionWrapRef.value;
        if (!wrap) return;

        const el =
          wrap.querySelector("textarea") ||
          wrap.querySelector('[contenteditable="true"]') ||
          wrap.querySelector("input");

        if (el && typeof el.focus === "function") {
          el.focus();
          if (typeof el.select === "function") el.select();
        }
      });
    });
  });
}


/**
 * Se a API retornou 409 com requiresConfirmation, abrimos o ConfirmAuthorDuplicateModal
 */
function maybeOpenConfirmFromError(error) {
  const data = error?.response?.data;
  if (error?.response?.status === 409 && data?.requiresConfirmation) {
    confirmMessage.value =
      data.message || "Confirme se deseja prosseguir com este nome.";
    existingAuthors.value = Array.isArray(data.existingAuthors)
      ? data.existingAuthors
      : [];
    showConfirmModal.value = true;
    return true;
  }
  return false;
}

// ⬇️ ATUALIZADO: usa allowDuplicateOnNextSubmit ao invés do parâmetro
async function edit() {
  if (isSubmitting.value) return;

  try {
    isSubmitting.value = true;

    normalizeName();

    // se o usuário já confirmou duplicidade, mandamos allowDuplicateName = true
    const allowDuplicateName = allowDuplicateOnNextSubmit.value === true;

    await authorStore.updateAuthor(obj.value, allowDuplicateName);

    showSuccessToast("Autor atualizado com sucesso.");
    hideBackdropForSuccess.value = true;

    setTimeout(() => {
      isSubmitting.value = false;
      emit("refresh");
      emit("close");
    }, 2400);
  } catch (error) {
    if (maybeOpenConfirmFromError(error)) {
      // 409 → abre modal de confirmação, não fecha o formulário
      isSubmitting.value = false;
      return;
    }

    let userFriendlyMessage =
      "Ocorreu um erro desconhecido ao salvar as alterações.";
    const errorData = error?.response?.data || error;

    if (errorData && errorData.errors) {
      const messages = Object.values(errorData.errors).flat();
      if (messages.length > 0) {
        userFriendlyMessage = messages.join("\n");
      }
    } else if (errorData && errorData.message) {
      userFriendlyMessage = errorData.message;
    } else if (error.message && typeof error.message === "string") {
      userFriendlyMessage = error.message;
    }

    console.error("Erro detalhado da API ao salvar:", errorData);
    showErrorToast(userFriendlyMessage);
    isSubmitting.value = false;
  }
}

// ⬇️ ATUALIZADO: não salva de imediato; só libera o próximo submit e foca descrição
async function confirmProceed() {
  // Usuário aceitou prosseguir mesmo com duplicados
  allowDuplicateOnNextSubmit.value = true;
  showConfirmModal.value = false;

  // Foca o campo de descrição para o usuário revisar/complementar
  focusDescriptionField();
}

/**
 * Chamado ao clicar em um card do modal de confirmação
 * → abre AuthorCardModal por cima
 */
function handleViewAuthor(authorId) {
  selectedAuthorId.value = authorId;
  showCardModal.value = true;
}

// ====================================================================
// FECHAMENTO DO MODAL PRINCIPAL
// ====================================================================
function closeModal() {
  // Não fecha se estiver submetendo ou se o modal de confirmação estiver aberto
  if (!isSubmitting.value && !showConfirmModal.value && !showCardModal.value) {
    emit("close");
  }
}

function handleEscKey(event) {
  if (event.key === "Escape") {
    closeModal();
  }
}

// ====================================================================
// CICLO DE VIDA
// ====================================================================
onMounted(async () => {
  try {
    const authorData = await authorStore.getAuthorById(authorStore.idAuthor);
    obj.value = { ...authorData };
  } catch (err) {
    console.error("Erro ao carregar autor:", err?.message || err);
    showErrorToast("Erro ao carregar dados do autor.");
  }

  nextTick(() => {
    if (nameInput.value) nameInput.value.focus();
  });

  document.addEventListener("keydown", handleEscKey);
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", handleEscKey);
  if (toastTimeoutId.value) {
    clearTimeout(toastTimeoutId.value);
  }
});
</script>

<template>
  <!-- MODAL PRINCIPAL DE EDIÇÃO -->
  <div
    v-show="!hideBackdropForSuccess"
    class="modal-backdrop"
    @click.self="closeModal"
  >
    <div ref="panelRef" class="modal-panel small" :style="panelStyle">
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h3 class="modal-title">Editar Autor</h3>
        <button
          @click="closeModal"
          type="button"
          class="modal-close-btn"
          :disabled="isSubmitting"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            ></path>
          </svg>
        </button>
      </div>

      <form @submit.prevent="edit(false)" novalidate>
        <div class="modal-body">
          <div class="form-group">
            <label for="name" class="form-label">Nome</label>
            <input
              ref="nameInput"
              type="text"
              name="name"
              id="name"
              v-model="obj.authorName"
              @blur="obj.authorName = toTitleCase(obj.authorName)"
              class="input-base"
              placeholder="Digite o nome do autor"
              required
              @focus="$event.target.select()"
            />
          </div>
          <div class="form-group">
            <label for="description" class="form-label"
              >Descrição (opcional)</label
            >
            <!-- ✅ PATCH: wrapper com ref para conseguir focar no elemento real -->
<div ref="descriptionWrapRef">
  <CustomTextarea
    v-model:body="obj.description"
    placeholder="Digite uma breve descrição (opcional)"
    :is-disabled="false"
    style="min-height: 120px"
  />
</div>

          </div>
        </div>

        <div class="modal-footer">
          <button
            type="button"
            @click="closeModal"
            class="btn btn-secondary"
            :disabled="isSubmitting"
          >
            Cancelar
          </button>
          <button
            type="submit"
            class="btn btn-success"
            :disabled="isSubmitting"
          >
            <svg
              v-if="isSubmitting"
              class="w-5 h-5 mr-2 animate-spin"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              ></path>
            </svg>

            <svg
              v-else
              class="w-5 h-5 mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                clip-rule="evenodd"
              />
            </svg>

            <span>
              {{ isSubmitting ? "Salvando..." : "Salvar Alterações" }}
            </span>
          </button>
        </div>
      </form>
    </div>
  </div>

  <!-- MODAL DE CONFIRMAÇÃO (DUPLICADOS) -->
  <Teleport to="body">
    <ConfirmAuthorDuplicateModal
      :show="showConfirmModal"
      :title="'Nome em uso: ' + toTitleCase(obj.authorName)"
      :message="confirmMessage"
      :existing-authors="existingAuthors"
      :is-submitting="isSubmitting"
      @close="showConfirmModal = false"
      @confirm="confirmProceed"
      @view-author="handleViewAuthor"
    />
  </Teleport>

  <!-- CARD DO AUTOR (ACIMA DO MODAL DE CONFIRMAÇÃO) -->
  <Teleport to="body">
    <AuthorCardModal
      v-if="showCardModal"
      :show="showCardModal"
      :author-id="selectedAuthorId"
      @close="showCardModal = false"
    />
  </Teleport>

  <!-- TOAST -->
  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro ao salvar" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>

          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            x
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>


